import React, { useState, useEffect, useRef } from 'react'
import { Button, Input, Spinner } from 'reactstrap'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { PaginationProps } from 'models/table.model'
// Type pour le filtre actif
type FilterStatus = { activeFilter: number | null }

const PaginationWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    color: var(--texte-table);
    font-weight: 600;
    font-size: 0.9rem;
    button {
      margin-right: 0.5rem;
    }
    span, div {
      display: flex;
      align-items: center;
      margin: 0.5rem;
    }
    select {
      max-width: 6.7rem;
    }
`

const LoadMoreSection = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  padding: 0 10px;
  background-color: #f8f9fa;
  border-radius: 5px;
  
  button {
    margin-left: 10px;
  }
`

const LoadingStatus = styled.div`
  display: flex;
  align-items: center;
  margin-left: 15px;
  
  .spinner {
    margin-left: 10px;
  }
`

const Pagination: React.FC<PaginationProps> = ({
  onGoToPage, 
  onNavigate, 
  offset, 
  pageCount, 
  isRowsPerPage, 
  setRowsPerPage, 
  currentRowsPerPage,
  // Nouvelles propriétés
  hasMore = false,
  loadingMore = false,
  isFullyLoaded = false,
  loadNextBatch,
  loadAllData,
  autoLoadingTriggered = false,
  loadedCount = 0,
  totalCount = 0,
  searchValue = '',
  activeFilter = 0,
  navLoading = false
}) => {
  const { formatMessage } = useIntl()
  const f = (id: any) => formatMessage({ id })
  const rowsPerPage = [5, 10, 20, 50]

  // Valeur saisie dans le champ "aller à la page" : le numéro défile librement
  // (flèches, saisie, appui maintenu) et la navigation n'est déclenchée qu'après
  // COMMIT_DELAY sans changement, pour ne lancer qu'un seul chargement
  const [pageInput, setPageInput] = useState<string>(String(offset + 1))
  const commitTimer = useRef<number | null>(null)
  const COMMIT_DELAY = 1000

  useEffect(() => {
    setPageInput(String(offset + 1))
  }, [offset])

  // Annuler un chargement programmé si le composant est démonté
  useEffect(() => {
    return () => {
      if (commitTimer.current !== null) window.clearTimeout(commitTimer.current)
    }
  }, [])

  const commitPageInput = (value: string): void => {
    const page = value === '' ? 0 : Number(value) - 1

    if (isNaN(page) || page < 0 || page > pageCount - 1) {
      setPageInput(String(offset + 1))
      return
    }

    if (page !== offset) onGoToPage(page)
  }

  // Chaque changement repousse le chargement : un appui maintenu sur la flèche
  // ne déclenche qu'une seule requête, sur la page finalement affichée
  const schedulePageCommit = (value: string): void => {
    if (commitTimer.current !== null) window.clearTimeout(commitTimer.current)
    commitTimer.current = window.setTimeout(() => {
      commitTimer.current = null
      commitPageInput(value)
    }, COMMIT_DELAY)
  }

  const commitPageInputNow = (): void => {
    if (commitTimer.current !== null) {
      window.clearTimeout(commitTimer.current)
      commitTimer.current = null
    }
    commitPageInput(pageInput)
  }

  return (
    <PaginationWrapper className='pagination'>
      <div>
        <Button onClick={() => onNavigate(-1)} disabled={offset <= 0 || navLoading}>
          {f('previousPagination')}
        </Button>{' '}
        <Button onClick={() => onNavigate(+1)} disabled={offset >= pageCount - 1 || navLoading}>
          {f('nextPagination')}
        </Button>{' '}
      </div>

      {/* Section pagination classique */}
      <div>
        <span>
          <span>{f('page')}</span>
          <strong>
            {offset + 1}{f('of')}{pageCount}
          </strong>{' '}
          {navLoading && <Spinner size='sm' className='ml-2' />}
        </span>
        <span>
          <span>{f('goToPage')}</span>
          <Input
            type='number'
            min={1}
            max={pageCount}
            value={pageInput}
            disabled={navLoading}
            onChange={e => {
              setPageInput(e.target.value)
              schedulePageCommit(e.target.value)
            }}
            onBlur={commitPageInputNow}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commitPageInputNow()
              }
            }}
            style={{ width: '100px' }}
          />
        </span>
      </div>

      {/* Sélection lignes par page */}
      {isRowsPerPage === true && setRowsPerPage && (
        <div>
          <span>{f('rowPerPage')}</span>
          <Input
            type='select'
            name='select'
            id='rowsPerPageSelect'
            onChange={(event) => {
              setRowsPerPage(parseInt(event.target.value))
            }}
          >
            {rowsPerPage.map((number, index) => {
              return (
                <option key={index} value={number} selected={currentRowsPerPage === number && true}>{number}</option>
              )
            })}
          </Input>
        </div>
      )}

      {/* Compteur de commandes chargées (mode chargement complet en arrière-plan) */}
      {autoLoadingTriggered && (
        <LoadMoreSection>
          <LoadingStatus>
            {f('autoLoading')}
            <Spinner size='sm' className='spinner' />
          </LoadingStatus>
        </LoadMoreSection>
      )}
    </PaginationWrapper>
  )
}

export default Pagination
